import { Component } from '@angular/core';
import { Genre, PartnerTags, Platform, ResearchCreator, ResearchCreatorStatus, Video } from '../../models/research';
import { ResearchService } from '../../services/research.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { KmbPipe } from '../../pipes/kmbPipe';
import { ChartComponent } from "../chart/chart.component";
import { ChartOptions } from 'chart.js';
import { DialogComponent } from '../dialog/dialog.component';

@Component({
  selector: 'app-research',
  imports: [FormsModule, CommonModule, KmbPipe, ChartComponent, DialogComponent],
  templateUrl: './research.component.html',
  styleUrl: './research.component.css'
})
export class ResearchComponent {

  // allCreators will already be filtered by Genre.
  allCreators: ResearchCreator[] = [];
  filteredCreators: ResearchCreator[] = [];

  selectedDayFilter: string = '';
  selectedMonthFilter: string = '';

  visibilityMap: { [key: string]: boolean } = {};

  paidAdVideos: Video[] = [];

  allPartnerTags: { [key: string]: number} = {};
  sortedTags: [string, number][] = [];

  selectedPartnerTagFilter: string = 'All';
  selectedStatusFilter = '';
  handleFilter: string = '';
  emailFilter: string = '';

  selectedCreator?: ResearchCreator;
  selectedHandle: string = '';
  selectedCreatorRowIndex: number = -1;

  filteredPartnerTags: PartnerTags[] = [];

  Platform = Platform;

  chartData: number[] = [];
  chartLabels: string[] = [];

  showingSingleMonthChart: boolean = false;

  selectedGenre: Genre = Genre.Finance;

  chartOptions: ChartOptions = {
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Campaigns by Month',
        font: {
          size: 18
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Month'
        },
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
          font: {
            size: 12
          }
        }
      },
      y: {
        title: {
          display: true,
          text: "Campaign Count"
        },
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  Genre=Genre;
  genreType = Object.entries(Genre)
  .filter(([key, value]) => isNaN(Number(key)))
  .map(([key, value]) => ({
    label: key.replace(/([A-Z])/g, ' $1').trim(),
    value
  }));

  ResearchCreatorStatus = ResearchCreatorStatus;
  researchCreatorStatus = Object.entries(ResearchCreatorStatus)
    .filter(([key, value]) => isNaN(Number(key))) // Exclude numeric keys if present
    .map(([key, value]) => ({
      label: key.replace(/([A-Z])/g, ' $1').trim(), // Format enum keys to human-readable text
      value, // Use the numeric value of the enum
    }));

  researchCreatorStatusFilter = Object.entries(ResearchCreatorStatus)
    .filter(([key, value]) => isNaN(Number(key))) // Exclude numeric keys if present
    .map(([key, value]) => ({
      label: key.replace(/([A-Z])/g, ' $1').trim(), // Format enum keys to human-readable text
      value, // Use the numeric value of the enum
    }));


  startDate: Date = new Date();
  endDate: Date = new Date();

  constructor(private researchService: ResearchService) {}

  ngOnInit(): void {
    this.researchCreatorStatusFilter.unshift({label: 'All', value: '' });
    this.setAllCreatorsByGenre();
  }

  // genreChange() {
  //   if (this.selectedPartnerTag === 'All') {
  //     this.filteredCreators = this.allCreators;
  //   } else {
  //     this.filteredCreators = this.allCreators.filter(creator =>
  //       creator.partnerTags.some(pt => pt.tag === this.selectedPartnerTag )
  //     );
  //     console.log("Filtered Creators:");
  //     console.table(this.filteredCreators);
  //   }
  // }

  setAllCreatorsByGenre() {
    this.researchService.getCreators(this.selectedGenre).subscribe({
      next: (result) => {
        this.selectedCreator = undefined;
        this.selectedStatusFilter = '';
        this.allCreators = result;
        this.filteredCreators = result;
        this.allPartnerTags = {};

        // Create all keys in the creatorVideos.
        this.allCreators.forEach(creator => {
          if (!creator.partnerTags) {
            creator.partnerTags = [];
          }

          creator.partnerTags.forEach(pt => {
            if (!(pt.tag in this.allPartnerTags)) {
              this.allPartnerTags[pt.tag] = 0;
            }
            this.allPartnerTags[pt.tag]++;
          });
        });

        this.updateFilteredCreators();
        this.setChartData();

        this.sortedTags = Object.entries(this.allPartnerTags).sort((a,b) => b[1] - a[1]);
        this.sortedTags.unshift(['All', 0]);
        this.selectedPartnerTagFilter = 'All';
      },
      error: (err) => {
        console.log(err);
      }
    })
  }

  handleChartClickNotify(bar: string) {
    if (bar.length > 0 && !this.showingSingleMonthChart) {
      // User has just clicked a single month - so now we are showing a single month.
      this.showingSingleMonthChart = true;
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const month = bar.split('-')[0];
      const year = 2000 + Number(bar.split('-')[1]);
      this.selectedMonthFilter = `${month}-${year}`;
      this.selectedDayFilter = '';
      this.chartOptions.plugins!.title!.text = 'Campaigns by Day';
      (this.chartOptions.scales!['x'] as any).title.text = 'Day';
      this.updateFilteredCreators();
      this.setChartData();
      this.showingSingleMonthChart = true;
    } else if (bar.length > 0 && this.showingSingleMonthChart) {
      // User has selected a single day. Show only campaigns for that day.
      // Filter both creators and show partnerTags that are from this single day.
      this.filteredPartnerTags = [];
      this.selectedDayFilter = bar;
      this.selectedMonthFilter = '';
      this.updateFilteredCreators();
      // Do not update chart if user clicks on a single day.
      //this.setChartData();
    }
    else {
      // User is moving back to campaigns by month chart. Reset day/month filters.
      this.selectedDayFilter = '';
      this.selectedMonthFilter = '';
      this.updateFilteredCreators();
      this.showingSingleMonthChart = false;
      this.chartOptions.plugins!.title!.text = 'Campaigns by Month';
      (this.chartOptions.scales!['x'] as any).title.text = 'Month';
      this.setChartData();
    }
  }

  filterPartnerTags(creator: ResearchCreator) {
    // called when user clicks on a handle in the list of creators.
    // In this case, we want to reset the filteredPartnerTags to show only that creators partnerTags
    this.filteredPartnerTags = creator.partnerTags;
    this.selectedHandle = creator.tiktok;
  }

  clearSelection() {
    this.selectedHandle = '';
    this.updateFilteredCreators();
  }

  showAddCreatorDialog = false;
  newCreatorTiktok = '';
  newCreatorGenre: Genre = Genre.Food;

  openAddCreatorDialog() {
    this.newCreatorTiktok = '';
    this.newCreatorGenre = Genre.Food;
    this.showAddCreatorDialog = true;
  }

  addCreatorClick() {
    if (!this.newCreatorTiktok.trim()) return;
    this.researchService.addCreator(this.newCreatorTiktok.trim(), Number(this.newCreatorGenre) as Genre).subscribe({
      next: (creator) => {
        this.showAddCreatorDialog = false;
        if (Number(creator.genre) === Number(this.selectedGenre)) {
          if (!creator.partnerTags) creator.partnerTags = [];
          this.allCreators.push(creator);
          this.updateFilteredCreators();
        }
      },
      error: (err) => {
        if (err.status === 400 && err.error) {
          try {
            const existing: ResearchCreator = typeof err.error === 'string' ? JSON.parse(err.error) : err.error;
            const genreLabel = this.genreType.find(g => g.value === existing.genre)?.label ?? existing.genre;
            console.warn(`Creator already exists. Handle: ${existing.tiktok}, Genre: ${genreLabel}`);
          } catch {
            console.error('Failed to add creator', err);
          }
        } else {
          console.error('Failed to add creator', err);
        }
      }
    });
  }

  updateFilteredCreators() {
    const dayFilter = this.selectedDayFilter;
    const monthFilter = this.selectedMonthFilter;
    const partnerTagFilter = this.selectedPartnerTagFilter;
    const statusFilter = this.selectedStatusFilter === '' ? null : Number(this.selectedStatusFilter);

    const filteredCreatorsSet = new Set<ResearchCreator>();
    const filteredPartnerTagsSet = new Set<PartnerTags>();

    this.allCreators.forEach(creator => {
      if (statusFilter != null && creator.status != statusFilter) {
        // continue to next iteration of forEach.
        return;
      }

      if (this.handleFilter && !creator.tiktok.toLowerCase().includes(this.handleFilter.toLowerCase())) {
        return;
      }

      if (this.emailFilter) {
        const latestEmail = creator.emailInBioChange?.slice(-1)[0]?.newEmail ?? '';
        if (!latestEmail.toLowerCase().includes(this.emailFilter.toLowerCase())) {
          return;
        }
      }

      if (creator.partnerTags.length === 0) {
        filteredCreatorsSet.add(creator);
        return;
      }

      creator.partnerTags.forEach(pt => {
        const date = new Date(pt.postDate);
        if (isNaN(date.getTime())) return;

        // PartnerTag filter
        if (partnerTagFilter !== 'All' && pt.tag !== partnerTagFilter) {
          return;
        }

        // Date filters
        if (dayFilter) {
          const month = date.toLocaleString('en-US', { month: 'short' });
          const day = date.getDate().toString();
          const formatted = `${month}-${day}`;
          if (formatted === dayFilter) {
            filteredCreatorsSet.add(creator);
            pt.handle = creator.tiktok;
            filteredPartnerTagsSet.add(pt);
          }
        } else if (monthFilter) {
          const month = date.toLocaleString('en-US', { month: 'short' });
          const year = date.getFullYear().toString();
          const formatted = `${month}-${year}`;
          if (formatted === monthFilter) {
            filteredCreatorsSet.add(creator);
            pt.handle = creator.tiktok;
            filteredPartnerTagsSet.add(pt);
          }
        } else {
          // No day or month filter - so include this partnerTag by default.#d4efdf
          pt.handle = creator.tiktok;
          filteredPartnerTagsSet.add(pt);
          filteredCreatorsSet.add(creator);
        }
      });
    });

    this.filteredCreators = Array.from(filteredCreatorsSet).sort(
      (a, b) => b.partnerTags.length - a.partnerTags.length
    );
    this.filteredPartnerTags = Array.from(filteredPartnerTagsSet).sort(
      (a, b) => new Date(b.postDate).getTime() - new Date(a.postDate).getTime()
    );

    // this.filteredCreators = this.allCreators.filter(creator => {

    //   if (statusFilter !== null && creator.status !== statusFilter) {
    //     return false;
    //   }

    //   var hashTagMatch = creator.partnerTags.some(pt => {
    //     const date = new Date(pt.postDate);
    //     if (isNaN(date.getTime())) return false;

    //     // PartnerTag filter
    //     if (partnerTagFilter !== 'All' && pt.tag !== partnerTagFilter) {
    //       return false;
    //     }

    //     // Date filters
    //     if (dayFilter) {
    //       const month = date.toLocaleString('en-US', { month: 'short' });
    //       const day = date.getDate().toString().padStart(2, '0');
    //       const formatted = `${month}-${day}`;
    //       return formatted === dayFilter;
    //     } else if (monthFilter) {
    //       const month = date.toLocaleString('en-US', { month: 'short' });
    //       const year = date.getFullYear().toString();
    //       const formatted = `${month}-${year}`;
    //       return formatted === monthFilter;
    //     }

    //     return true;
    //   });

    //   return hashTagMatch;
    // });

    // this.filteredPartnerTags = this.filteredCreators
    // .flatMap(c => c.partnerTags.map(tag => ({ ... tag, handle: c.tiktok })))
    // .filter(tag => {
    //   const date = new Date(tag.postDate);
    //   if (isNaN(date.getTime())) return false;

    //   const matchesTag = partnerTagFilter === 'All' || tag.tag === partnerTagFilter;

    //   let matchesDate = true;
    //   if (dayFilter) {
    //     const month = date.toLocaleString('en-US', { month: 'short' });
    //     const day = date.getDate().toString().padStart(2, '0');
    //     const formatted = `${month}-${day}`;
    //     return formatted === dayFilter;
    //   } else if (monthFilter) {
    //     const month = date.toLocaleString('en-US', { month: 'short' });
    //     const year = date.getFullYear().toString(); // Full year now
    //     const formatted = `${month}-${year}`;
    //     return formatted === monthFilter;
    //   }

    //   return matchesTag && matchesDate;
    // });

    //this.setChartData();
  }

  applyTextFilters() {
    this.updateFilteredCreators();

    // Rebuild sortedTags to reflect only the filtered creators
    const tagCounts: { [key: string]: number } = {};
    this.filteredCreators.forEach(creator => {
      creator.partnerTags.forEach(pt => {
        tagCounts[pt.tag] = (tagCounts[pt.tag] || 0) + 1;
      });
    });
    this.sortedTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]);
    this.sortedTags.unshift(['All', 0]);

    // Update the Campaigns by Month chart for the filtered set
    this.setChartData();
  }

  setChartData() {
    // Chart data will represent counts from filteredPartnerTags only.
    var dates: Date[] = [];



    // this.filteredCreators.forEach(creator => {
    //   creator.partnerTags.forEach(pt => {
    //     if (pt.tag === this.selectedPartnerTagFilter || this.selectedPartnerTagFilter === 'All') {
    //       const parsedDate = new Date(pt.postDate); // Convert to Date object
    //       dates.push(parsedDate);
    //     }
    //   });
    // });

    this.filteredPartnerTags.forEach(pt => {
      dates.push(new Date(pt.postDate));
    });

    const counts = new Map<string, number>();

    // Iterate over dates and count occurrences by "MMM-YY" format
    dates.forEach(date => {
      // may be a month key or a day key.  mmm-yy or mmm-dd
      var key = '';
      if (this.showingSingleMonthChart) {
        // day key mmm-dd
        key = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).replace(/\s/g, '-');
      } else {
        // month key mmm-yy
        key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }).replace(/\s/g, '-');
      }

      counts.set(key, (counts.get(key) || 0) + 1);
    });

    // Generate full list of labels (months or days) in the desired range
    const allLabels: string[] = [];

    let currentDate = new Date(Math.min(...dates.map(d => d.getTime()))); // Start from earliest date
    let endDate = new Date(Math.max(...dates.map(d => d.getTime()))); // End at latest date

    if (this.showingSingleMonthChart) {
      currentDate = new Date(dates[0].getFullYear(), dates[0].getMonth(), 1);
      endDate = new Date(dates[0].getFullYear(), dates[0].getMonth()+1, 0)
    }

    // Ensure all months (or days) are included from start to end
    if (currentDate && endDate) {
      // Reset time components to 00:00:00 for accurate comparison>
      currentDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      while (currentDate <= endDate) {
        if (this.showingSingleMonthChart) {
          const dayKey = currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).replace(/\s/g, '-');
          allLabels.push(dayKey);
          currentDate.setDate(currentDate.getDate() + 1); // Move to next day.
        } else {
          const monthKey = currentDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }).replace(/\s/g, '-');
          allLabels.push(monthKey);
          currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1); // Move to the start of the next month
        }
      }
    }

    // Assign data to component properties, filling missing months with 0
    this.chartLabels = allLabels;
    this.chartData = allLabels.map(label => counts.get(label) ?? 0);
  }

  // setChartDataSingleMonth(month: number, year: number) {
  //   const date = new Date(year, month, 1);
  //   const labels: string[] = [];

  //   while (date.getMonth() === month) {
  //     const label = date.toLocaleDateString('en-US', {
  //       month: 'short',
  //       day: 'numeric',
  //     }).replace(/\s/g, '-');

  //     labels.push(label);
  //     date.setDate(date.getDate() + 1);
  //   }

  //   const countMap: { [key: string]: number } = {};

  //   this.filteredCreators.forEach(creator => {
  //     creator.partnerTags.forEach(pt => {
  //       if (pt.tag === this.selectedPartnerTagFilter || this.selectedPartnerTagFilter === 'All') {
  //         const parsedDate = new Date(pt.postDate);
  //         if (parsedDate.getFullYear() === year) {
  //           const key = parsedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric'}).replace(/\s/g, '-');
  //           countMap[key] = (countMap[key] || 0) + 1;
  //         }
  //       }
  //     });
  //   });

  //   const values: number[] = labels.map(label => countMap[label] || 0);

  //   // Assign data to component properties, filling missing months with 0
  //   this.chartLabels = labels;
  //   this.chartData = values;
  // }

  isBetween(date: Date, start: Date, end: Date): boolean {
    return date >= start && date <= end;
  }

  getVideoLink(creatorId: string, videoId: string) {
    var link = `https://tiktok.com/${creatorId}/video/${videoId}`;
    return link;
  }

  toggleVisibility(creatorId: string) {
    this.visibilityMap[creatorId] = !this.visibilityMap[creatorId];

    var creator = this.allCreators.find(c => c.id===creatorId);

    if (creator === undefined) {
      throw "Creator not found";
    }

  }

  updateStatus(creator: ResearchCreator) {
    this.researchService.updateResearchCreator(creator).subscribe({
      next: (result) => {
        console.log(`Updated: ${result}`);
      },
      error: (err) => {
        console.log(err);
      }
    });
  }

  updateGenre(creator: ResearchCreator) {
    this.researchService.updateResearchCreator(creator).subscribe({
      next: (result) => {
        console.log(`Updated: ${result}`);
        this.filteredCreators = this.filteredCreators.filter(c => c.id !== creator.id);
      },
      error: (err) => {
        console.log(err);
      }
    });
  }

  getBackgroundColor(status: ResearchCreatorStatus): string {
    if (status === ResearchCreatorStatus.Unset) {
      return "#f5b7b1";
    } else if (status === ResearchCreatorStatus.ReachedOut) {
      return "#d4efdf";
    } else if (status === ResearchCreatorStatus.Revisit) {
      return "#fffdb2";
    }

    return "white";
  }

  setSelectedHandle(handle: string) {
    this.selectedHandle = handle;
  }

  setSelectedHashtag(hashtag: string) {
    // update filteredCreators to only show those with this hashtag.
    var creators: ResearchCreator[] = [];

    this.allCreators.forEach(c => {
      if (c.partnerTags.some(pt => pt.tag === hashtag)) {
        creators.push(c);
      }
    });

    this.filteredCreators = creators;
  }

  tiktokClick(creator: ResearchCreator) {
    this.selectedCreator = creator;
  }



  deleteCreator(creator: ResearchCreator) {
    creator.deleted = true;

    this.researchService.deleteCreator(creator.id).subscribe({
      next: (result) => {
        console.log(`Deleted creator ${result}`);
        this.filteredCreators = this.filteredCreators.filter(c => c.id !== creator.id);
      },
      error: (err) => {
        console.log(err);
      }
    });
  }

  downloadPartnerTags(): void {
    this.downloadCSV(this.filteredPartnerTags);
  }

  downloadCreators(): void {
    this.downloadCSV(this.filteredCreators);
  }

  downloadCSV(data: any[]): void {
    const csvData = this.convertToCSV(data); // 'this.data' is your table data
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger the download
    const a = document.createElement('a');
    a.href = url;
    a.download = 'data.csv';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  }



  convertToCSV_OLD(objArray: any[]): string {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let csv = '';

    // Create header row
    const headers = Object.keys(array[0]);
    csv += headers.join(',') + '\r\n';

    // Create data rows
    array.forEach((item: { [x: string]: any; }) => {
      const row = headers.map(header => item[header]).join(',');
      csv += row + '\r\n';
    });
    return csv;
  }

 convertToCSV(objArray: any[]): string {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
  if (!array.length) return '';

  const hasEmailInBioChange = 'emailInBioChange' in array[0];

  const baseHeaders = Object.keys(array[0])
    .filter(header => header !== 'id' && header !== 'emailInBioChange'); // Remove 'id' and 'emailInBioChange'

  const hasPostDate = baseHeaders.includes('postDate');

  // Add 'Month' column after 'postDate' if applicable
  const headers = [...baseHeaders];
  if (hasPostDate) {
    const index = headers.indexOf('postDate');
    headers.splice(index + 1, 0, 'Month');
  }

  // Replace emailInBioChange with emailInBio + dateEmailChanged columns, placed right after 'tiktok'
  if (hasEmailInBioChange) {
    const tiktokIndex = headers.indexOf('tiktok');
    if (tiktokIndex !== -1) {
      headers.splice(tiktokIndex + 1, 0, 'emailInBio', 'dateEmailChanged');
    } else {
      headers.push('emailInBio');
      headers.push('dateEmailChanged');
    }
  }

  let csv = headers.join(',') + '\r\n';

  array.forEach((item: { [key: string]: any }) => {
    // Pre-compute latest emailInBioChange entry once per row
    const emailEntries = hasEmailInBioChange && Array.isArray(item['emailInBioChange']) ? item['emailInBioChange'] : [];
    const latestEmailEntry = emailEntries.length > 0 ? emailEntries[emailEntries.length - 1] : undefined;

    const row = headers.map(header => {
      if (header === 'partnerTags' && Array.isArray(item['partnerTags'])) {
        return item['partnerTags'].length;
      }

      if (header === 'Month' && hasPostDate && item['postDate']) {
        const date = new Date(item['postDate']);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-01-${year}`;
      }

      if (header === 'emailInBio') {
        const email = (latestEmailEntry?.newEmail ?? '').toString();
        if (email.includes(',') || email.includes('"')) {
          return `"${email.replace(/"/g, '""')}"`;
        }
        return email;
      }

      if (header === 'dateEmailChanged') {
        // Try both 'changeDate' (Cosmos field name) and 'dateChanged' (C# serialized name)
        const dateVal = (latestEmailEntry?.changeDate ?? latestEmailEntry?.dateChanged ?? '').toString();
        if (dateVal.includes(',') || dateVal.includes('"')) {
          return `"${dateVal.replace(/"/g, '""')}"`;
        }
        return dateVal;
      }

      if (header === 'genre') {
        const genreKey = Genre[item['genre']];
        if (genreKey !== undefined) {
          return genreKey.replace(/([A-Z])/g, ' $1').trim();
        }
        return item['genre'];
      }

      if (header === 'status') {
        const statusKey = ResearchCreatorStatus[item['status']];
        if (statusKey !== undefined) {
          return statusKey.replace(/([A-Z])/g, ' $1').trim();
        }
        return item['status'];
      }

      const value = item[header];

      if (typeof value === 'object' && value !== null) return '';

      if (typeof value === 'string') {
        let escaped = value.replace(/"/g, '""');
        if (escaped.includes(',') || escaped.includes('"')) {
          escaped = `"${escaped}"`;
        }
        return escaped;
      }

      return value;
    });

    csv += row.join(',') + '\r\n';
  });

  return csv;
}





}
